/**
 * Shared Validation Utilities
 * Feature: 015-manual-crud-entities - Phase 7 Polish (T119)
 * 
 * Common validation schemas and patterns used across forms
 * Ensures consistency with backend FluentValidation rules
 */

import { z } from 'zod';

/**
 * Validation Regex Patterns
 */
export const ValidationPatterns = {
  /**
   * International phone format: +[country][number]
   * Matches backend pattern in CreateAccountRequestValidator
   * Examples: +15551234567, +442071234567, +81312345678
   */
  PHONE: /^\+?[1-9]\d{1,14}$/,

  /**
   * Subject trimming check - no leading/trailing whitespace
   */
  NO_WHITESPACE_PADDING: (val: string) => val.trim() === val,
} as const;

/**
 * Standard Field Length Limits
 * Matches backend MaxLength validators
 */
export const FieldLimits = {
  SUBJECT: { MIN: 3, MAX: 200 },
  EMAIL: { MAX: 255 },
  PHONE: { MAX: 50 },
  COMPANY_NAME: { MAX: 255 },
  CONTACT_NAME: { MAX: 100 },
  COUNTRY: { MAX: 100 },
  CITY: { MAX: 100 },
  STREET: { MAX: 500 },
} as const;

/**
 * Reusable Zod Schema Builders
 */

/**
 * Email field validator
 * - Required or optional
 * - Valid email format
 * - Max 255 characters
 * - Converts to lowercase
 */
export const emailSchema = (required: boolean = true) => {
  const base = z
    .string()
    .email('Invalid email format')
    .max(FieldLimits.EMAIL.MAX, `Email must not exceed ${FieldLimits.EMAIL.MAX} characters`)
    .toLowerCase();

  return required ? base.min(1, 'Email is required') : base.optional().or(z.literal(''));
};

/**
 * Phone field validator
 * - Required or optional
 * - International format with optional + prefix
 * - Max 50 characters
 */
export const phoneSchema = (required: boolean = true) => {
  const base = z
      .string()
      .max(FieldLimits.PHONE.MAX, `Phone must not exceed ${FieldLimits.PHONE.MAX} characters`);

  return required
      ? base.min(1, 'Phone is required')
      : base.optional().or(z.literal(''));
};

/**
 * Subject field validator (for Leads and Tickets)
 * - Required
 * - Min 3, Max 200 characters
 * - No leading/trailing whitespace
 * - Trimmed
 */
export const subjectSchema = () =>
  z
    .string()
    .min(FieldLimits.SUBJECT.MIN, `Subject must be at least ${FieldLimits.SUBJECT.MIN} characters`)
    .max(FieldLimits.SUBJECT.MAX, `Subject must not exceed ${FieldLimits.SUBJECT.MAX} characters`)
    .refine(
      ValidationPatterns.NO_WHITESPACE_PADDING,
      'Subject must not have leading or trailing whitespace'
    );

/**
 * Company name validator
 * - Required
 * - Max 255 characters
 * - Trimmed
 */
export const companyNameSchema = () =>
  z
    .string()
    .min(1, 'Company name is required')
    .max(
      FieldLimits.COMPANY_NAME.MAX,
      `Company name must not exceed ${FieldLimits.COMPANY_NAME.MAX} characters`
    )
    .trim();

/**
 * Contact name validator
 * - Optional
 * - Max 100 characters
 */
export const contactNameSchema = () =>
  z
    .string()
    .max(
      FieldLimits.CONTACT_NAME.MAX,
      `Contact name must not exceed ${FieldLimits.CONTACT_NAME.MAX} characters`
    )
    .optional();

/**
 * Country validator
 * - Required
 * - Max 100 characters
 */
export const countrySchema = () =>
  z
    .string()
    .min(1, 'Country is required')
    .max(FieldLimits.COUNTRY.MAX, `Country must not exceed ${FieldLimits.COUNTRY.MAX} characters`)
    .trim();

/**
 * City validator
 * - Optional - accepts empty string from form
 * - Max 100 characters
 */
export const citySchema = () =>
  z.string().max(FieldLimits.CITY.MAX, `City must not exceed ${FieldLimits.CITY.MAX} characters`);

/**
 * Street address validator
 * - Optional - accepts empty string from form
 * - Max 500 characters
 */
export const streetSchema = () =>
  z.string().max(FieldLimits.STREET.MAX, `Street must not exceed ${FieldLimits.STREET.MAX} characters`);

/**
 * Description validator (optional text field)
 * - Optional
 * - No length limit (uses TEXT in database)
 */
export const descriptionSchema = () => z.string().optional();

/**
 * Account linking XOR validator helper
 * Ensures either accountId OR newAccount is provided, but not both
 */
export const createAccountLinkingRefine = (data: {
  accountLinkMode: 'existing' | 'new';
  accountId?: string;
  newAccountCompanyName?: string;
  newAccountEmail?: string;
  newAccountPhone?: string;
}) => {
  if (data.accountLinkMode === 'existing') {
    return !!data.accountId;
  } else if (data.accountLinkMode === 'new') {
    return (
      !!data.newAccountCompanyName && !!data.newAccountEmail && !!data.newAccountPhone
    );
  }
  return false;
};

/**
 * Error messages for account linking validation
 */
export const ACCOUNT_LINKING_ERROR = {
  message: 'Must provide either an existing account or complete new account information',
  path: ['accountId'],
};
